import { BrowserModule } from "@angular/platform-browser";
import { NgModule, inject, provideAppInitializer } from "@angular/core";
import { AppRoutingModule } from "./app-routing.module";
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from "@angular/common/http";
import { SafeHtmlPipe } from './safe-html.pipe';
import { UserRoleListComponent } from './user-role-list/user-role-list.component';
import { SessionExpiredComponent } from './session-expired/session-expired.component';
import { MasterDataService } from "./services/masterdata.service";
import { DatePipe, CommonModule } from "@angular/common";
import { RouterModule, TitleStrategy } from "@angular/router";
import { AppComponent } from "./app.component";
import { LoaderIconComponent } from './loader-icon/loader-icon.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AddSessionComponent } from './add-session/add-session.component';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { ResourcesComponent } from './resources/resources.component';
import { SmartDataGridComponent } from "./smart-data-grid/smart-data-grid.component";
import { NgbDateParserFormatter, NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { NgbToDatePipe } from './pipes/ngb-to-date.pipe';
import { DateToNgbPipe } from './pipes/date-to-ngb.pipe';
import { AuditTrailComponent } from './audit-trail/audit-trail.component';
import { NgSelectModule } from "@ng-select/ng-select";
import { AuthGuard } from "./Auth/auth.guard";
import { SearchSessionComponent } from "./search-session/search-session.component";
import { SubscriptionComponent } from "./subscription/subscription/subscription.component";
import { SearchSubscriptionComponent } from "./search-subscription/search-subscription.component";
import { AddEditPricingModalComponent } from "./subscription/add-edit-pricing-modal/add-edit-pricing-modal.component";
import { AdditionalQuestionsComponent } from './additional-questions/additional-questions.component';
import { DatacleanUpComponent } from "./dataclean-up/dataclean-up.component";
import { ManageEmailTemplateComponent } from "./manage-email-template/manage-email-template.component";
import { ManageMailboxComponent } from "./manage-mailbox/manage-mailbox.component";
import { UpdateEmailAddressComponent } from "./update-email-address/update-email-address.component";
import { SearchEmailTemplateComponent } from "./search-email-template/search-email-template.component";
import { CreateOrganizationComponent } from "./organization/create-organization/create-organization.component";
import { SearchOrganizationComponent } from "./organization/search-organization/search-organization.component";
import { SessionTimeoutComponent } from "./session-timeout/session-timeout.component";
import { SecurityComponent } from "./security/security.component";
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { NgMultiselectDropdownDirective } from './directives/ng-multiselect-dropdown.directive';
import { FacilitatorSearchComponent } from './facilitator-search/facilitator-search.component';
import { EditFacilitatorComponent } from "./edit-facilitator/edit-facilitator.component";
import { AssessmentComponent } from './assessment/assessment.component';
import { TeamSnapshotComponent } from "./team-snapshot/team-snapshot.component";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { LangTranslateModule } from "./lang-translate/lang-translate.module";
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { RichTextEditorComponent } from './rich-text-editor/rich-text-editor.component';
import { ReplaceText } from "./pipes/replace-text.pipe";
import { MsalRedirectComponent } from "@azure/msal-angular";
import { appInitializerFactory } from "./core/factories/app-initializer.factory";
import { AppConfigService } from "./core/services/app-config.service";
import { MsalConfigModule } from "./msal-config/msal-config.module";
import { userRoleInterceptor } from "./core/interceptors/user-role.interceptor";
import { ClickOutsideDirective } from "./directives/click-outside.directive";
import { CustomDateParserFormatter } from "./services/date.formatter";
import { TemplatePageTitleStrategy } from "./services/template-page-title-strategy.service";
import { UserUnauthorizedComponent } from "./user-unauthorized/user-unauthorized.component";
import { UnauthorisedComponent } from "./unauthorised/unauthorised.component";
import { sessionResetInterceptor } from "./core/interceptors/session-reset.interceptor";
import { tokenInterceptor } from "./core/interceptors/token.interceptor";
import { CookieNoticeComponent } from './cookie-notice/cookie-notice.component';

@NgModule({
  declarations: [
    ConfirmationDialogComponent,
    ResourcesComponent,
    UserRoleListComponent,
    AuditTrailComponent,
    SessionExpiredComponent,
    SafeHtmlPipe,
    AppComponent,
    LoaderIconComponent,
    AddSessionComponent,
    SmartDataGridComponent,
    NgbToDatePipe,
    DateToNgbPipe,
    SearchSessionComponent,
    SubscriptionComponent,
    SearchSubscriptionComponent,
    AddEditPricingModalComponent,
    AdditionalQuestionsComponent,
    DatacleanUpComponent,
    ManageEmailTemplateComponent,
    ManageMailboxComponent,
    UpdateEmailAddressComponent,
    SearchEmailTemplateComponent,
    CreateOrganizationComponent,
    SearchOrganizationComponent,
    SessionTimeoutComponent,
    SecurityComponent,
    NgMultiselectDropdownDirective,
    FacilitatorSearchComponent,
    EditFacilitatorComponent,
    AssessmentComponent,
    TeamSnapshotComponent,
    RichTextEditorComponent,
    ReplaceText,
    ClickOutsideDirective,
    UserUnauthorizedComponent,
    UnauthorisedComponent,
    CookieNoticeComponent
  ],
  imports: [
    NgMultiSelectDropDownModule,
    RouterModule.forRoot([]),
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    NgbModule,
    CommonModule,
    NgSelectModule,
    ReactiveFormsModule,
    DragDropModule,
    MsalConfigModule.forRoot(),
    LangTranslateModule,
    EditorModule
  ],
  providers: [
    MasterDataService,
    DatePipe,
    NgbToDatePipe,
    DateToNgbPipe,
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'assets/tinymce/tinymce.min.js' },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
    {provide: TitleStrategy, useClass: TemplatePageTitleStrategy},
    AuthGuard,
    provideAppInitializer(() => appInitializerFactory(inject(AppConfigService), undefined)()),
    provideHttpClient(withXhr(), 
      withInterceptorsFromDi(),
      withInterceptors([userRoleInterceptor, sessionResetInterceptor, tokenInterceptor])
    ),
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule { }
